function createImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener("load", () => resolve(image));
        image.addEventListener("error", (err) => reject(err));
        image.crossOrigin = "anonymous";
        image.src = url;
    });
}

function getRadianAngle(degreeValue) {
    return (degreeValue * Math.PI) / 180;
}

// returns the bounding box of a rotated rectangle, needed so the rotated
// image isn't clipped before we crop it
function rotatedSize(width, height, rotation) {
    const rotRad = getRadianAngle(rotation);

    return {
        width:
            Math.abs(Math.cos(rotRad) * width) +
            Math.abs(Math.sin(rotRad) * height),
        height:
            Math.abs(Math.sin(rotRad) * width) +
            Math.abs(Math.cos(rotRad) * height)
    };
}

/**
 * Crops an image (data URL) to the given pixel area, applying rotation first.
 * @param {string} imageSrc - data URL of the source image
 * @param {{x:number,y:number,width:number,height:number}} croppedAreaPixels - from react-easy-crop's onCropComplete
 * @param {number} rotation - degrees, from react-easy-crop's rotation state
 * @param {string} mimeType - output mime type
 */
export default async function getCroppedImg(
    imageSrc,
    croppedAreaPixels,
    rotation = 0,
    mimeType = "image/jpeg"
) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const { width: boxWidth, height: boxHeight } = rotatedSize(
        image.width,
        image.height,
        rotation
    );

    // draw the full rotated image onto an oversized canvas first
    canvas.width = boxWidth;
    canvas.height = boxHeight;

    ctx.translate(boxWidth / 2, boxHeight / 2);
    ctx.rotate(getRadianAngle(rotation));
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    // pull out just the cropped region (coordinates already account for rotation,
    // since react-easy-crop reports croppedAreaPixels relative to the rotated image)
    const cropped = ctx.getImageData(
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height
    );

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    ctx.putImageData(cropped, 0, 0);

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error("Canvas is empty"));
                    return;
                }
                blob.name = "photo.jpg";
                resolve(blob);
            },
            mimeType,
            0.92
        );
    });
}
